import React, { useEffect, useState } from "react";
import profilePicture from "../../../resources/avatarShuffle.webp";
import { useLazyQuery } from "@apollo/client";
import { HOMEPAGE_LEADERBOARD_RAINGG } from "../../../queries/queries";
import { number_format } from "../../../helpers";
import Countdown, { zeroPad } from "react-countdown";
import moment from "moment";
import { Helmet } from "react-helmet";
import { websiteName } from "../../../config";
import raincoinSVG from "../../../resources/coinRAINGG.svg";

const RainggLeaderboard = ({ showMinimalDetails }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardRemaining, setLeaderboardRemaining] = useState([]);
  const [leaderboardFirst, setLeaderboardFirst] = useState();
  const [leaderboardSecond, setLeaderboardSecond] = useState();
  const [leaderboardThird, setLeaderboardThird] = useState();
  const [nextResetTimestamp, setNextResetTimestamp] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [totalPrize, setTotalPrize] = useState(0);
  const [countdownTarget, setCountdownTarget] = useState(null);
  const [countdownLabel, setCountdownLabel] = useState("LEADERBOARD ENDS IN");

  const [getLeaderboard] = useLazyQuery(HOMEPAGE_LEADERBOARD_RAINGG, {
    fetchPolicy: "cache-and-network",
    onCompleted: (e) => {
        const rows = (e.getLeaderboardRaingg || []).map(r => ({
            ...r,
            Prize: typeof r.Prize === "number" ? r.Prize / 100 : 0,
        }));
        setLeaderboard(rows);

        // Helper: safe numeric
        const num = (v) => (v === null || v === undefined ? NaN : Number(v));

        // If backend sends epoch ms (numbers/strings), use those directly.
        let startsMs = Math.min(
            ...rows
            .map(r => num(r.StartsAt))
            .filter(v => Number.isFinite(v))
        );
        let endsMs = Math.max(
            ...rows
            .map(r => num(r.EndsAt))
            .filter(v => Number.isFinite(v))
        );

        // Fallback parser for string timestamps like "YYYY-MM-DD HH:mm:ss"
        if (!Number.isFinite(startsMs) || !Number.isFinite(endsMs)) {
            const parseTs = (s) => {
            if (s == null) return null;
            if (typeof s === 'number') return s;                 // epoch ms
            if (/^\d+$/.test(s)) return parseInt(s, 10);         // numeric string ms
            // normalize to ISO UTC
            const iso = s.includes('T') ? s : s.replace(' ', 'T');
            const withZ = /Z$|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z';
            const m = moment.utc(withZ);
            return m.isValid() ? m.valueOf() : null;
            };
            const startsList = rows.map(r => parseTs(r.StartsAt)).filter(v => Number.isFinite(v));
            const endsList   = rows.map(r => parseTs(r.EndsAt)).filter(v => Number.isFinite(v));
            if (startsList.length) startsMs = Math.min(...startsList);
            if (endsList.length)   endsMs   = Math.max(...endsList);
        }

        const nowMs = Date.now();

        if (Number.isFinite(startsMs) && nowMs < startsMs) {
            setCountdownTarget(new Date(startsMs));
            setCountdownLabel("LEADERBOARD STARTS IN");
        } else if (Number.isFinite(endsMs) && nowMs < endsMs) {
            setCountdownTarget(new Date(endsMs));
            setCountdownLabel("LEADERBOARD ENDS IN");
        } else if (Number.isFinite(startsMs) || Number.isFinite(endsMs)) {
            setCountdownTarget(null);
            setCountdownLabel("LEADERBOARD ENDED");
        } else {
            // no dates found
            setCountdownTarget(null);
            setCountdownLabel("LEADERBOARD ENDS IN");
        }
    },
    onError: () => console.log("API Request failed - if this keeps happening, report to developer!"),
    });

  const renderer = ({ formatted: { days, hours, minutes, seconds } }) => (
    <div className="cdWrapper">
      <div>
        <div className="cdValue">{zeroPad(days)}</div>
        <div className="cdText">Days</div>
      </div>
      <div>
        <div className="cdValue">{zeroPad(hours)}</div>
        <div className="cdText">Hours</div>
      </div>
      <div>
        <div className="cdValue">{zeroPad(minutes)}</div>
        <div className="cdText">Mins</div>
      </div>
      <div>
        <div className="cdValue">{zeroPad(seconds)}</div>
        <div className="cdText">Secs</div>
      </div>
    </div>
  );

  useEffect(() => {
    if (leaderboard && leaderboard.length > 0) {
      setLeaderboardFirst(leaderboard.slice(0, 1)[0]);
      setLeaderboardSecond(leaderboard.slice(1, 2)[0]);
      setLeaderboardThird(leaderboard.slice(2, 3)[0]);
      setLeaderboardRemaining(leaderboard.slice(3, 50));

      // prize pool = sum of prizes we got back
      const sum = leaderboard.reduce((acc, e) => acc + (e.Prize || 0), 0);
      setTotalPrize(sum);
    } else {
      setLeaderboardFirst(undefined);
      setLeaderboardSecond(undefined);
      setLeaderboardThird(undefined);
      setLeaderboardRemaining([]);
      setTotalPrize(0);
    }
  }, [leaderboard]);

  useEffect(() => {
    getLeaderboard(); // or pass variables if you want a specific month
    }, [getLeaderboard]);

  const safeAvatar = (url) => {
    if (!url) return profilePicture;
    // RainGG returns full URLs already
    return url;
  };

  return (
    <div className={`fadeIn lb-roobet`}>
      <Helmet>
        <title>{`${websiteName} | Rain.GG Leaderboard`}</title>
      </Helmet>

      <div className="lbTotalAmount" style={{ textAlign: "center", marginTop: 30 }}>
        <span className="neonHeaderText">
          <h1 style={{ fontSize: "80px" }}>
            <img src={raincoinSVG} style={{ height: "70px", marginRight: "20px" }} alt="" />
            {number_format(totalPrize)}
          </h1>
        </span>
      </div>

      {!showMinimalDetails && (
        <div className="lb-countdown">
            <div className="countdownHeader">{countdownLabel}</div>
            {countdownTarget ? (
            <Countdown zeroPadTime={1} date={countdownTarget} renderer={renderer} />
            ) : (
            <div className="cdWrapper">
                <div className="cdText">—</div>
            </div>
            )}
        </div>
        )}

      <div className="lb-podium">
        <div className="entry">
          {leaderboardSecond && (
            <>
              <div className="profileWrapper">
                <div className="profilePictureWrapper">
                  <img
                    src={safeAvatar(leaderboardSecond.PlayerImage)}
                    className={`profilePicture`}
                    alt=""
                  />
                </div>
                <div className={"positionWrapper"}>2</div>
                <p>{leaderboardSecond.PlayerName}</p>
              </div>
              <div className="wageredWrapper">
                <p className="wageredText">Wagered</p>
                <p className="wageredValue">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardSecond.WageredAmount)}
                </p>
              </div>
              <div className="prizeWrapper">
                <div className="prize">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardSecond.Prize || 0)}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="entry">
          {leaderboardFirst && (
            <>
              <div className="profileWrapper">
                <div className="profilePictureWrapper">
                  <img
                    src={safeAvatar(leaderboardFirst.PlayerImage)}
                    className={`profilePicture`}
                    alt=""
                  />
                </div>
                <div className={"positionWrapper"}>1</div>
                <p>{leaderboardFirst.PlayerName}</p>
              </div>
              <div className="wageredWrapper">
                <p className="wageredText">Wagered</p>
                <p className="wageredValue">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardFirst.WageredAmount)}
                </p>
              </div>
              <div className="prizeWrapper">
                <div className="prize">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardFirst.Prize || 0)}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="entry">
          {leaderboardThird && (
            <>
              <div className="profileWrapper">
                <div className="profilePictureWrapper">
                  <img
                    src={safeAvatar(leaderboardThird.PlayerImage)}
                    className={`profilePicture`}
                    alt=""
                  />
                </div>
                <div className={"positionWrapper"}>3</div>
                <p>{leaderboardThird.PlayerName}</p>
              </div>
              <div className="wageredWrapper">
                <p className="wageredText">Wagered</p>
                <p className="wageredValue">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardThird.WageredAmount)}
                </p>
              </div>
              <div className="prizeWrapper">
                <div className="prize">
                  <img src={raincoinSVG} style={{ height: "16px" }} alt="" />{" "}
                  {number_format(leaderboardThird.Prize || 0)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {!showMinimalDetails && lastSyncTime && (
        <div className="lb-countdown">
          <div className="cdWrapper">
            <div className="cdText">Last updated {lastSyncTime}</div>
          </div>
        </div>
      )}

      {!showMinimalDetails && (
        <div className="lb-leaderboardRest">
          <div className="tableHeader">
            <div className="name">User</div>
            <div className="wagered">Wagered</div>
            <div className="prize">Prize</div>
          </div>
          <div className="tableContent">
            {leaderboardRemaining.map((entry, idx) => (
              <div className="tableRow" key={idx}>
                <div className="name">
                  <div className="playerPos">#{idx + 4}</div>
                  <div className="playerImage">
                    {/* Keep hidden like Cases; enable if you want avatar here */}
                    {/* <img src={safeAvatar(entry.PlayerImage)} className={`profilePicture`} alt=""/> */}
                  </div>
                  <div className="playerName">{entry.PlayerName}</div>
                </div>
                <div className="wagered">
                  <span className="greenDollar"><img src={raincoinSVG} style={{ height: "16px" }} alt="" /></span> {number_format(entry.WageredAmount)}
                </div>
                <div className="prize">
                  <span className="greenDollar"><img src={raincoinSVG} style={{ height: "16px" }} alt="" /></span> {number_format(entry.Prize || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RainggLeaderboard;
